/**
 * Universal Vital Signs Box Component
 * Reusable component for every vital-signs display, built on one FHIR-based
 * data structure; used on the dashboard, Health Records, etc.
 */

import { VitalSignsChart } from "@/components/charts/VitalSignsChart";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { unifiedVitalSignsService } from "@/services/fhir-vitals.service";
import { useI18nStore } from "@/stores/i18nStore";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import {
  VitalSignsChartData,
  VitalSignsData,
  VitalSignsInput,
} from "@/types/fhir-vitals";
import {
  Activity,
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  Edit3,
  Heart,
  Save,
  TrendingUp,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";

export interface VitalSignsBoxProps {
  // Display mode
  mode?: "dashboard" | "health-records" | "standalone";

  // Layout options
  showChart?: boolean;
  showEditButton?: boolean;
  showHeader?: boolean;
  compactMode?: boolean;

  // Data props
  initialData?: VitalSignsData;

  // Event Callbacks
  onSave?: (data: VitalSignsData) => void;
  onError?: (message: string) => void;

  // Styling
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
}

export const VitalSignsBox: React.FC<VitalSignsBoxProps> = ({
  mode = "standalone",
  showChart = true,
  showEditButton = true,
  showHeader = true,
  compactMode = false,
  initialData,
  onSave,
  onError,
  className = "",
  headerClassName = "",
  contentClassName = "",
}) => {
  // Internationalization
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();

  // State Management
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Vital Signs Data
  const [currentVitalSigns, setCurrentVitalSigns] =
    useState<VitalSignsData | null>(null);

  // Edit Mode Data
  const [editData, setEditData] = useState<{
    systolicBloodPressure?: number | undefined;
    diastolicBloodPressure?: number | undefined;
    restingHeartRate?: number | undefined;
  }>({});

  // Chart Data
  const [chartData, setChartData] = useState<VitalSignsChartData[]>([]);

  // Validation Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load data on component mount
  useEffect(() => {
    if (initialData) {
      setCurrentVitalSigns(initialData);
    } else {
      loadLatestVitalSigns();
    }
  }, [initialData]);

  // Load chart data when needed
  useEffect(() => {
    if (showChart && !compactMode) {
      loadChartData();
    }
  }, [showChart, compactMode]);

  /**
   * Load latest vital signs from unified service
   */
  const loadLatestVitalSigns = async () => {
    try {
      setIsLoading(true);
      const vitalSigns = await unifiedVitalSignsService.getLatestVitalSigns();
      setCurrentVitalSigns(vitalSigns);
    } catch (error: any) {
      console.error("Error loading vital signs:", error);
      onError?.(t.vitalLoadError);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load chart data for trend visualization (365 days) - UPDATED v1.2.0
   */
  const loadChartData = async () => {
    try {
      setIsLoadingHistory(true);
      const data = await unifiedVitalSignsService.getVitalSignsChartData(365);
      setChartData(data);
    } catch (error: any) {
      console.error("Error loading chart data:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  /**
   * Start edit mode
   */
  const handleStartEdit = () => {
    if (!currentVitalSigns) return;

    setEditData({
      systolicBloodPressure: currentVitalSigns.systolicBloodPressure,
      diastolicBloodPressure: currentVitalSigns.diastolicBloodPressure,
      restingHeartRate: currentVitalSigns.restingHeartRate,
    });
    setIsEditMode(true);
    setErrors({});
  };

  /**
   * Cancel edit mode
   */
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData({});
    setErrors({});
  };

  /**
   * Save vital signs
   */
  const handleSave = async () => {
    if (
      !editData.systolicBloodPressure ||
      !editData.diastolicBloodPressure ||
      !editData.restingHeartRate
    ) {
      setErrors({ save: t.allFieldsRequired });
      return;
    }

    // Local validation for edit data
    const validationErrors: Record<string, string> = {};
    if (
      !editData.systolicBloodPressure ||
      editData.systolicBloodPressure < 70 ||
      editData.systolicBloodPressure > 250
    ) {
      validationErrors.systolic = t.systolicBpError;
    }
    if (
      !editData.diastolicBloodPressure ||
      editData.diastolicBloodPressure < 40 ||
      editData.diastolicBloodPressure > 150
    ) {
      validationErrors.diastolic = t.diastolicBpError;
    }
    if (
      !editData.restingHeartRate ||
      editData.restingHeartRate < 30 ||
      editData.restingHeartRate > 220
    ) {
      validationErrors.heartRate = t.heartRateError;
    }
    if (
      editData.systolicBloodPressure &&
      editData.diastolicBloodPressure &&
      editData.systolicBloodPressure <= editData.diastolicBloodPressure
    ) {
      validationErrors.pressure = t.systolicMustBeHigher;
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSaving(true);

      const dataToSave: VitalSignsInput = {
        systolicBloodPressure: editData.systolicBloodPressure,
        diastolicBloodPressure: editData.diastolicBloodPressure,
        restingHeartRate: editData.restingHeartRate,
      };

      const response =
        await unifiedVitalSignsService.saveVitalSigns(dataToSave);

      if (response.success) {
        // Reload latest vital signs from database to ensure accuracy
        const latestData = await unifiedVitalSignsService.getLatestVitalSigns();

        if (latestData) {
          setCurrentVitalSigns(latestData);
        } else {
          // Fallback: use the saved data
          const updatedVitalSigns: VitalSignsData = {
            systolicBloodPressure: dataToSave.systolicBloodPressure!,
            diastolicBloodPressure: dataToSave.diastolicBloodPressure!,
            bloodPressureUnit: "mmHg",
            restingHeartRate: dataToSave.restingHeartRate!,
            heartRateUnit: "bpm",
            recordedAt: response.data.recordedAt || new Date().toISOString(),
            fhirStatus: "final",
          };
          setCurrentVitalSigns(updatedVitalSigns);
        }

        setIsEditMode(false);
        setEditData({});
        setErrors({});

        // Reload chart data
        if (showChart && !compactMode) {
          await loadChartData();
        }

        // Call parent callback
        if (latestData) {
          onSave?.(latestData);
        }
      }
    } catch (error: any) {
      console.error("Error saving vital signs:", error);
      setErrors({ save: t.vitalSaveError });
      onError?.(t.vitalSaveError);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Get component styling based on mode
   */
  const getComponentStyling = () => {
    const baseClasses = "bg-white rounded-lg shadow-sm border";

    switch (mode) {
      case "dashboard":
        return compactMode ? `${baseClasses} p-4` : `${baseClasses} p-6`;
      case "health-records":
        return `${baseClasses} p-6`;
      case "standalone":
      default:
        return `${baseClasses} p-6`;
    }
  };

  /**
   * Format timestamp for display
   */
  const formatTimestamp = (timestamp: string) => {
    try {
      // Locale-aware date+time in the app language (MM/DD/YYYY, 12-hour
      // clock for English).
      return fmt.dateTime(timestamp);
    } catch {
      return timestamp;
    }
  };

  if (isLoading) {
    return (
      <div className={`${getComponentStyling()} ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  // A reading missing any of the three values cannot be classified; the
  // service returns undefined for absent measurements rather than substituting
  // defaults, so this renders the same empty state as no reading at all.
  const hasCompleteReading =
    currentVitalSigns?.systolicBloodPressure !== undefined &&
    currentVitalSigns?.diastolicBloodPressure !== undefined &&
    currentVitalSigns?.restingHeartRate !== undefined;

  if (!currentVitalSigns || !hasCompleteReading) {
    return (
      <div className={`${getComponentStyling()} ${className}`}>
        <div className="text-center py-8">
          <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">{t.noVitalSignsData}</p>
          {showEditButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleStartEdit}
              className="mt-4"
            >
              {t.recordFirstMeasurement}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const status = unifiedVitalSignsService.calculateVitalSignsStatus(
    currentVitalSigns.systolicBloodPressure!,
    currentVitalSigns.diastolicBloodPressure!,
    currentVitalSigns.restingHeartRate!,
  );

  return (
    <div className={`${getComponentStyling()} ${className}`}>
      {/* Header */}
      {showHeader && (
        <div
          className={`px-6 py-4 border-b border-gray-200 ${headerClassName}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Activity className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3
                  className={`font-semibold text-gray-900 ${compactMode ? "text-base" : "text-lg"}`}
                >
                  {t.vitalSigns}
                </h3>
                {!compactMode && currentVitalSigns.recordedAt && (
                  <div className="flex items-center space-x-1 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>
                      {t.lastMeasurement}:{" "}
                      {formatTimestamp(currentVitalSigns.recordedAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Edit Controls */}
            {showEditButton && (
              <div className="flex items-center space-x-2">
                {!isEditMode ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStartEdit}
                    disabled={isLoading}
                    className="flex items-center space-x-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>{t.edit}</span>
                  </Button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="flex items-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>{t.cancel}</span>
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={isSaving}
                      className="flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>{isSaving ? t.saving : t.save}</span>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`p-6 ${contentClassName}`}>
        {/* Vital Signs Values */}
        <div
          className={`grid gap-6 mb-6 ${compactMode ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}
        >
          {/* Blood Pressure */}
          <div className={`p-4 rounded-lg ${status.bloodPressure.bgColor}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="flex items-center space-x-2">
                <Heart className={`w-5 h-5 ${status.bloodPressure.color}`} />
                <span className="font-medium text-gray-900">
                  {t.bloodPressure}
                </span>
              </div>
              <span
                className={`text-sm font-medium px-2 py-1 rounded whitespace-nowrap ${status.bloodPressure.color} bg-white`}
              >
                {t[status.bloodPressure.label as keyof typeof t] ||
                  status.bloodPressure.label}
              </span>
            </div>

            {isEditMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t.systolic} (mmHg)
                    </label>
                    <Input
                      type="number"
                      min="70"
                      max="250"
                      value={editData.systolicBloodPressure || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditData((prev) => ({
                          ...prev,
                          systolicBloodPressure: value
                            ? parseInt(value)
                            : undefined,
                        }));
                      }}
                      className={errors.systolic ? "border-red-300" : ""}
                      placeholder="120"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t.diastolic} (mmHg)
                    </label>
                    <Input
                      type="number"
                      min="40"
                      max="150"
                      value={editData.diastolicBloodPressure || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditData(
                          (prev) =>
                            ({
                              ...prev,
                              diastolicBloodPressure: value
                                ? parseInt(value)
                                : undefined,
                            }) as typeof editData,
                        );
                      }}
                      className={errors.diastolic ? "border-red-300" : ""}
                      placeholder="80"
                    />
                  </div>
                </div>
                {(errors.systolic || errors.diastolic || errors.pressure) && (
                  <div className="text-xs text-red-600">
                    {errors.systolic || errors.diastolic || errors.pressure}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {currentVitalSigns.systolicBloodPressure} /{" "}
                  {currentVitalSigns.diastolicBloodPressure}
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    mmHg
                  </span>
                </div>
                {currentVitalSigns.recordedAt && (
                  <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatTimestamp(currentVitalSigns.recordedAt)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Heart Rate */}
          <div className={`p-4 rounded-lg ${status.heartRate.bgColor}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="flex items-center space-x-2">
                <Heart className={`w-5 h-5 ${status.heartRate.color}`} />
                <span className="font-medium text-gray-900">
                  {t.restingHeartRate}
                </span>
              </div>
              <span
                className={`text-sm font-medium px-2 py-1 rounded whitespace-nowrap ${status.heartRate.color} bg-white`}
              >
                {t[status.heartRate.label as keyof typeof t] ||
                  status.heartRate.label}
              </span>
            </div>

            {isEditMode ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t.restingHeartRate} ({t.bpm})
                  </label>
                  <Input
                    type="number"
                    min="30"
                    max="220"
                    value={editData.restingHeartRate || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditData(
                        (prev) =>
                          ({
                            ...prev,
                            restingHeartRate: value
                              ? parseInt(value)
                              : undefined,
                          }) as typeof editData,
                      );
                    }}
                    className={errors.heartRate ? "border-red-300" : ""}
                    placeholder="70"
                  />
                </div>
                {errors.heartRate && (
                  <div className="text-xs text-red-600">{errors.heartRate}</div>
                )}
              </div>
            ) : (
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {currentVitalSigns.restingHeartRate}
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    {t.bpm}
                  </span>
                </div>
                {currentVitalSigns.recordedAt && (
                  <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatTimestamp(currentVitalSigns.recordedAt)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error Messages */}
        {errors.save && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <div className="text-sm text-red-700">{errors.save}</div>
            </div>
          </div>
        )}

        {/* Chart Section */}
        {showChart && !compactMode && (
          <div className="mt-6">
            <div className="flex items-center space-x-2 mb-4">
              <TrendingUp className="w-5 h-5 text-gray-600" />
              <h4 className="text-lg font-medium text-gray-900">
                {t.vitalSignsHistory}
              </h4>
              <Calendar className="w-4 h-4 text-gray-400" />
            </div>

            {isLoadingHistory ? (
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="animate-pulse text-gray-500">
                  {t.loadingHistory}
                </div>
              </div>
            ) : chartData.length > 0 ? (
              <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
                <VitalSignsChart data={chartData} />
              </div>
            ) : (
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>{t.noHistoryData}</p>
                  <p className="text-sm">{t.firstMeasurementsWillAppear}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compact mode footer with timestamp */}
        {compactMode && currentVitalSigns.recordedAt && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{formatTimestamp(currentVitalSigns.recordedAt)}</span>
              </div>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
